import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';
import { GetBeacon4SidePolygons } from '../requests/Beacon4PerformanceRequests';

export const loadBeacon4SidePolygons = createAsyncThunk(
	'beacon4Filters/loadSidePolygons',
	async (_, { rejectWithValue }) => {
		try {
			const res = await GetBeacon4SidePolygons();
			return res?.data ?? res;
		} catch (e) {
			return rejectWithValue(e.response?.data ?? e.message);
		}
	}
);

const beacon4FiltersSlice = createSlice({
	name: 'beacon4Filters',
	initialState: {
		dateFrom:    moment().subtract(1, 'months').format('YYYY-MM-DD'),
		dateTo:      moment().format('YYYY-MM-DD'),
		aggregation: 'Daily',
		side:        '',  // comma-separated for multi-select
		polygon:     '',
		jvid:        '',

		// All pairs from DB — never changes after load
		allPairs: [],   // [{ side, polygon, jvid }]

		// Derived locally
		sides:    [],
		polygons: [],
		allJvids: [],   // all JV IDs — always available
		jvids:    [],   // filtered JV IDs based on side+polygon

		visibleSeries: null,        // null = all visible; array = only these shown
		loading:       false,
		error:         null,
	},
	reducers: {
		setBeacon4Aggregation(state, action) { state.aggregation = action.payload; },
		setBeacon4DateFrom(state, action)    { state.dateFrom    = action.payload; },
		setBeacon4DateTo(state, action)      { state.dateTo      = action.payload; },

		setBeacon4Side(state, action) {
			state.side    = action.payload;
			state.polygon = '';
			state.jvid    = '';
			const selectedSides = action.payload
				? action.payload.split(',').map(v => v.trim()).filter(Boolean)
				: [];
			// Derive polygons from selected sides
			state.polygons = [...new Set(
				state.allPairs
					.filter(p => selectedSides.length === 0 || selectedSides.includes(p.side))
					.map(p => p.polygon)
			)].sort();
			// Derive jvids from selected sides (no polygon filter yet)
			state.jvids = [...new Set(
				state.allPairs
					.filter(p => selectedSides.length === 0 || selectedSides.includes(p.side))
					.map(p => p.jvid ?? p.Jvid)
					.filter(Boolean)
			)].sort();
		},

		setBeacon4Polygon(state, action) {
			state.polygon = action.payload;
			state.jvid    = '';
			const selectedSides    = state.side ? state.side.split(',').map(v => v.trim()).filter(Boolean) : [];
			const selectedPolygons = action.payload ? action.payload.split(',').map(v => v.trim()).filter(Boolean) : [];
			state.jvids = [...new Set(
				state.allPairs
					.filter(p =>
						(selectedSides.length === 0    || selectedSides.includes(p.side)) &&
						(selectedPolygons.length === 0 || selectedPolygons.includes(p.polygon))
					)
					.map(p => p.jvid ?? p.Jvid)
					.filter(Boolean)
			)].sort();
		},

		setBeacon4Jvid(state, action) {
			state.jvid = action.payload;
			const selectedJvids = action.payload
				? action.payload.split(',').map(v => v.trim()).filter(Boolean)
				: [];
			if (selectedJvids.length > 0) {
				// Re-derive sides and polygons from selected jvids
				const matchingPairs = state.allPairs.filter(p =>
					selectedJvids.includes(p.jvid ?? p.Jvid)
				);
				// Only restrict sides/polygons if no side/polygon already selected
				if (!state.side) {
					state.sides    = [...new Set(matchingPairs.map(p => p.side))].sort();
					state.polygons = [...new Set(matchingPairs.map(p => p.polygon))].sort();
				} else {
					// Side already selected — just filter polygons
					const selectedSides = state.side.split(',').map(v => v.trim()).filter(Boolean);
					state.polygons = [...new Set(
						matchingPairs
							.filter(p => selectedSides.includes(p.side))
							.map(p => p.polygon)
					)].sort();
				}
			} else {
				// No jvid selected — restore full sides/polygons
				state.sides    = [...new Set(state.allPairs.map(p => p.side))].sort();
				if (state.side) {
					const selectedSides = state.side.split(',').map(v => v.trim()).filter(Boolean);
					state.polygons = [...new Set(
						state.allPairs.filter(p => selectedSides.includes(p.side)).map(p => p.polygon)
					)].sort();
				} else {
					state.polygons = [];
				}
			}
		},

		toggleBeacon4Series(state, action) {
			const key = action.payload;
			if (state.visibleSeries === null) {
				// First click — isolate this series only
				state.visibleSeries = [key];
			} else if (state.visibleSeries.includes(key)) {
				// Already visible — remove it
				const next = state.visibleSeries.filter(k => k !== key);
				state.visibleSeries = next.length > 0 ? next : null;
			} else {
				// Add to visible
				state.visibleSeries = [...state.visibleSeries, key];
			}
		},
		resetBeacon4HiddenSeries(state) { state.visibleSeries = null; },
	},
	extraReducers: (b) => {
		b.addCase(loadBeacon4SidePolygons.pending, (s) => { s.loading = true; s.error = null; })
		 .addCase(loadBeacon4SidePolygons.fulfilled, (s, a) => {
				s.loading  = false;
				s.allPairs = a.payload ?? [];
				s.sides    = [...new Set((a.payload ?? []).map(p => p.side))].sort();
				s.allJvids = [...new Set((a.payload ?? []).map(p => p.jvid ?? p.Jvid).filter(Boolean))].sort();
				if (s.side) {
					const selectedSides = s.side.split(',').map(v => v.trim()).filter(Boolean);
					s.polygons = [...new Set(
						(a.payload ?? []).filter(p => selectedSides.includes(p.side)).map(p => p.polygon)
					)].sort();
					if (s.polygon) {
						const sel = s.polygon.split(',').map(v => v.trim()).filter(Boolean);
						s.jvids = [...new Set(
							(a.payload ?? [])
								.filter(p => selectedSides.includes(p.side) && (sel.length === 0 || sel.includes(p.polygon)))
								.map(p => p.jvid ?? p.Jvid)
								.filter(Boolean)
						)].sort();
					} else {
						s.jvids = [...new Set(
							(a.payload ?? []).filter(p => selectedSides.includes(p.side)).map(p => p.jvid ?? p.Jvid).filter(Boolean)
						)].sort();
					}
				} else {
					s.jvids = s.allJvids;
				}
			})
		 .addCase(loadBeacon4SidePolygons.rejected, (s, a) => { s.loading = false; s.error = a.payload; });
	},
});

export const {
	setBeacon4Aggregation,
	setBeacon4DateFrom, setBeacon4DateTo,
	setBeacon4Side, setBeacon4Polygon, setBeacon4Jvid,
	toggleBeacon4Series, resetBeacon4HiddenSeries,
} = beacon4FiltersSlice.actions;

export default beacon4FiltersSlice.reducer;

export const selectBeacon4Filters       = (state) => state.beacon4Filters;
export const selectBeacon4VisibleSeries = (state) => state.beacon4Filters.visibleSeries;
export const selectBeacon4AllJvids      = (state) => state.beacon4Filters.allJvids;